import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useBlocker } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import type { ISpProfilePublic } from '@brushfire/types'
import { usePatchSpDemographics } from '@/features/rewards/api/profile-mutations'
import { useUploadSpAvatar } from '@/features/account/api/avatar-mutations'
import { AccountDemographicForm } from '@/features/account/components/AccountDemographicForm'
import { AccountProfileCard } from '@/features/account/components/AccountProfileCard'
import { DemographicFieldList } from '@/features/account/components/DemographicFieldList'
import type { AccountEditingGuard } from '@/features/account/lib/account-editing-guard'
import { formToPatchPayload, profileToFormValues } from '@/features/account/lib/account-form-utils'
import {
  accountSettingsSchema,
  type AccountSettingsFormData,
} from '@/features/account/lib/account-settings-schema'
import {
  accountDetailsTabSx,
  accountEditActionsInnerSx,
  accountEditActionsSx,
  accountEditCancelButtonSx,
  accountEditSaveButtonSx,
} from '@/features/account/lib/account-settings-tokens'
import { areEkycNameFieldsLocked } from '@/features/ekyc/lib/ekycUi'
import { ConfirmCancelModal } from '@/shared/components/confirmation'
import { useNotify } from '@/shared/toast/ToastProvider'

interface Props {
  profile: ISpProfilePublic
  onEditingGuardChange?: (guard: AccountEditingGuard | null) => void
}

export function AccountDetailsTab({ profile, onEditingGuardChange }: Readonly<Props>) {
  const notify = useNotify()
  const [isEditing, setIsEditing] = useState(false)
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false)
  const pendingLeaveActionRef = useRef<(() => void) | null>(null)

  const patchDemographics = usePatchSpDemographics()
  const uploadAvatar = useUploadSpAvatar()

  const form = useForm<AccountSettingsFormData>({
    resolver: zodResolver(accountSettingsSchema),
    defaultValues: profileToFormValues(profile),
    mode: 'onChange',
  })

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = form
  const stateValue = watch('state')
  const nameFieldsLocked = areEkycNameFieldsLocked(profile.ekycStatus)

  useEffect(() => {
    reset(profileToFormValues(profile))
  }, [profile, reset])

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isEditing &&
      isDirty &&
      currentLocation.pathname !== nextLocation.pathname,
  )

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setUnsavedDialogOpen(true)
    }
  }, [blocker.state])

  useEffect(() => {
    if (!isEditing || !isDirty) {
      onEditingGuardChange?.(null)
      return
    }
    onEditingGuardChange?.({
      isBlocking: true,
      requestLeave: (onLeave) => {
        pendingLeaveActionRef.current = onLeave
        setUnsavedDialogOpen(true)
      },
    })
    return () => onEditingGuardChange?.(null)
  }, [isEditing, isDirty, onEditingGuardChange])

  const exitEditMode = useCallback(() => {
    reset(profileToFormValues(profile))
    setIsEditing(false)
  }, [profile, reset])

  const onSave = handleSubmit(async (values) => {
    try {
      await patchDemographics.mutateAsync(
        formToPatchPayload(values, { omitNameFields: nameFieldsLocked }),
      )
      notify.showToast('Account updated successfully', 'success')
      setIsEditing(false)
    } catch {
      notify.showToast(
        'Unable to save changes. Please check your connection and try again.',
        'error',
      )
    }
  })

  const handleCancel = () => {
    if (isDirty) {
      setUnsavedDialogOpen(true)
      return
    }
    exitEditMode()
  }

  const handleUnsavedLeave = () => {
    setUnsavedDialogOpen(false)
    exitEditMode()
    if (blocker.state === 'blocked') blocker.proceed()
    pendingLeaveActionRef.current?.()
    pendingLeaveActionRef.current = null
  }

  const handleUnsavedStay = () => {
    setUnsavedDialogOpen(false)
    pendingLeaveActionRef.current = null
    if (blocker.state === 'blocked') blocker.reset()
  }

  return (
    <Box data-testid="account-details-tab" sx={isEditing ? undefined : accountDetailsTabSx}>
      <AccountProfileCard
        profile={profile}
        isEditing={isEditing}
        onEdit={() => setIsEditing(true)}
        onAvatarUpload={async (file) => {
          await uploadAvatar.mutateAsync(file)
          notify.showToast('Profile picture updated', 'success')
        }}
        isAvatarUploading={uploadAvatar.isPending}
      />
      {isEditing ? (
        <AccountDemographicForm
          control={control}
          errors={errors}
          stateValue={stateValue}
          email={profile.email}
          setValue={setValue}
          nameFieldsLocked={nameFieldsLocked}
        />
      ) : (
        <DemographicFieldList profile={profile} />
      )}

      {isEditing ? (
        <Box sx={accountEditActionsSx}>
          <Box sx={accountEditActionsInnerSx}>
            <Button
              variant="text"
              sx={accountEditCancelButtonSx}
              onClick={handleCancel}
              disabled={patchDemographics.isPending}
              data-testid="account-cancel"
            >
              Cancel
            </Button>
            <Button
              sx={accountEditSaveButtonSx}
              onClick={() => void onSave()}
              disabled={patchDemographics.isPending}
              data-testid="account-save-changes"
            >
              {patchDemographics.isPending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Save Changes'
              )}
            </Button>
          </Box>
        </Box>
      ) : null}

      <ConfirmCancelModal
        open={unsavedDialogOpen}
        headline="Do you want to cancel?"
        bodyLines={['Are you sure you want to cancel?', 'Your progress will be lost.']}
        stayLabel="Stay"
        confirmLabel="Confirm"
        onStay={handleUnsavedStay}
        onConfirm={handleUnsavedLeave}
        dialogTestId="account-unsaved-dialog"
        stayTestId="account-unsaved-stay"
        confirmTestId="account-unsaved-confirm"
      />
    </Box>
  )
}
